<script lang="ts" setup>
import type { SelectedRegionsSectionClick } from '.';
import type { Region } from '@@/mocks/static/regions';
import type Carousel from '@/components/ui/carousel/Carousel.vue';

import { Container } from '@/components/Container';
import { Button } from '@/components/ui/button';
import { CarouselGenericAction } from '@/components/ui/carousel/CarouselGenericAction';
import { CarouselItemSelector } from '@/components/ui/carousel/CarouselItemSelector';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

import { SelectedRegionsSectionSkeleton } from '.';
import SelectedRegionsSectionBadgeCarousel from './SelectedRegionsSectionBadgeCarousel.vue';
import SelectedRegionsSectionCategories from './SelectedRegionsSectionCategories.vue';
import SelectedRegionsSectionText from './SelectedRegionsSectionText.vue';

type ViewMoreClickedParams = {
	href: string;
	clickCount: number;
	ctaText: string;
};

const props = defineProps<{
	regions: Region[];
	isLoading: boolean;
}>();

const emit = defineEmits<{
	(e: 'cta-view-more-clicked', params: ViewMoreClickedParams): void;
	(
		e: 'carousel_neighborhood_clicked',
		params: SelectedRegionsSectionClick
	): void;
}>();

// Constants
const SLIDE_DELAY = 2000;

// Refs and States
const carouselContainerRef = ref<InstanceType<typeof Carousel> | null>(null);

const carouselItemSelectorContainerRef = ref<HTMLDivElement | null>(null);

const clickCount = ref(0);

const {
	currentCarouselItemIndex,
	scrollableSlidesNumber,
	carouselDynamicItemWidth,
	scrollTo
} = useCarouselControl({
	carouselContainerRef,
	carouselItemSelectorContainerRef
});

const currentRegion = computed(
	() => props.regions[currentCarouselItemIndex.value]!
);

function handleViewMoreClicked({
	href,
	ctaText
}: Omit<ViewMoreClickedParams, 'clickCount'>) {
	clickCount.value += 1;
	emit('cta-view-more-clicked', {
		href,
		clickCount: clickCount.value,
		ctaText
	});
}

const neighborhoodCategoryClickedEventBus =
	useEventBus<SelectedRegionsSectionClick>('neighborhood_category_clicked');

neighborhoodCategoryClickedEventBus.on(
	({ clickLocation, cardText, neighborhoodName, destinationUrl }) =>
		handleCarouselNeighborhoodClicked({
			clickLocation,
			cardText,
			neighborhoodName,
			destinationUrl
		})
);

function handleCarouselNeighborhoodClicked({
	clickLocation,
	cardText,
	neighborhoodName,
	destinationUrl
}: SelectedRegionsSectionClick) {
	emit('carousel_neighborhood_clicked', {
		clickLocation,
		cardText,
		neighborhoodName,
		destinationUrl
	});
}

function scrollSlides(index: number) {
	const totalSlides = scrollableSlidesNumber.value;
	const cyclicIndex = (index + totalSlides) % totalSlides;

	scrollTo(cyclicIndex, false);
}
</script>

<template>
	<section class="relative h-[727px] w-full bg-white-dark-1 sm:h-[744px]">
		<Container>
			<SelectedRegionsSectionSkeleton v-if="isLoading" />
			<template v-else>
				<header class="flex flex-col items-start pb-9">
					<Skeleton
						:is-active="isLoading"
						variant="white"
						:class="isLoading ? 'h-[34px] w-1/2 md:w-[359px]' : ''"
					>
						<h2
							class="font-matter-sq text-[28px] font-normal text-primary md:text-[40px]"
						>
							Bairros em destaque
						</h2>
					</Skeleton>
				</header>

				<Carousel
					ref="carouselContainerRef"
					class="relative h-[440px] w-full sm:h-auto"
					:plugins="[WheelGesturesPlugin()]"
					:opts="{ align: 'center', loop: true }"
				>
					<CarouselContent class="h-full">
						<CarouselItem
							v-for="(region, index) in regions"
							:key="region.title"
							class="relative h-[440px]"
						>
							<NuxtLink
								:to="region.url"
								class="relative flex size-full cursor-pointer items-center justify-center"
								@click="
									handleCarouselNeighborhoodClicked({
										clickLocation: 'neighborhood card',
										neighborhoodName: region.title,
										destinationUrl: region.url || ''
									})
								"
							>
								<div
									class="absolute inset-0 bg-linear-to-b from-black/70 via-black/20 to-black/70"
								/>
								<NuxtImg
									:src="region.image || ''"
									width="1440"
									height="440"
									sizes="xs:375px sm:640px md:768px lg:1024px xl:1280px 2xl:1440px"
									densities="x1 x2"
									format="webp"
									class="size-full object-cover brightness-65 contrast-100 grayscale"
									:quality="index !== 0 ? 85 : 60"
									loading="lazy"
								/>
							</NuxtLink>
						</CarouselItem>
					</CarouselContent>
					<div
						class="pointer-events-none absolute left-0 top-0 h-[440px] w-full sm:h-full"
					>
						<div
							class="no-scrollbar z-40 flex w-auto items-center py-4 md:py-6"
						>
							<SelectedRegionsSectionBadgeCarousel
								ref="badgeCarouselComponentRef"
								:carousel-item-index="currentCarouselItemIndex"
								:parent-carousel-item-index="currentCarouselItemIndex"
								:regions="regions"
								@scroll-slide="scrollSlides"
							>
							</SelectedRegionsSectionBadgeCarousel>
						</div>
						<div
							class="absolute bottom-0 left-0 flex h-[379px] w-full flex-col justify-end gap-6 overflow-hidden py-4 md:py-6 md:pl-2 xl:flex-row xl:items-end xl:justify-between xl:gap-0 xl:pl-6"
						>
							<NuxtLink
								:to="currentRegion.url"
								class="pointer-events-auto"
								@click="
									handleCarouselNeighborhoodClicked({
										clickLocation: 'neighborhood title',
										neighborhoodName: currentRegion?.title,
										destinationUrl: currentRegion.url || ''
									})
								"
							>
								<SelectedRegionsSectionText
									class="w-full max-w-[470px] px-4 md:pr-6"
									:title="currentRegion.title"
									:description="currentRegion.description"
								/>
							</NuxtLink>
							<div
								class="no-scrollbar pointer-events-auto w-full overflow-x-auto px-4"
							>
								<SelectedRegionsSectionCategories
									class="min-w-[776px] justify-end md:min-w-[unset] md:justify-start xl:justify-end"
									:categories="currentRegion.categories || []"
									:region-url="currentRegion.url || ''"
									:region-title="currentRegion.title"
								/>
							</div>
						</div>
					</div>

					<div
						class="absolute mt-6 flex h-auto w-full flex-col-reverse items-center gap-5 sm:left-auto sm:mt-9 sm:flex-row sm:justify-between md:mx-0"
					>
						<NuxtLink
							v-slot="{ href }"
							:to="PropertyFiltersPresenter.DEFAULT_URL_PATH"
							custom
							class="self-start"
						>
							<Button
								as="a"
								:href="href"
								variant="primary"
								class="h-[48px] w-[231px] sm:h-[52px] md:w-[267px]"
								:is-skeleton-loading="isLoading"
								skeleton-classes="h-[48px] w-[231px] sm:h-[52px] md:w-[267px]"
								skeleton-variant="white"
								@click="handleViewMoreClicked"
							>
								Ver todos os imóveis
								<IconMaterialSymbolsNorthEast class="w-5 md:w-6" />
							</Button>
						</NuxtLink>
						<div class="flex w-full items-center gap-5">
							<div
								ref="carouselItemSelectorContainerRef"
								class="flex w-full flex-wrap justify-end gap-3"
							>
								<CarouselItemSelector
									v-for="(_, index) in scrollableSlidesNumber"
									:key="`selector-${index}`"
									:active="currentCarouselItemIndex === index"
									:delay="SLIDE_DELAY"
									variant="dark"
									:dynamic-width="carouselDynamicItemWidth"
									@click="scrollSlides(index)"
								/>
							</div>
							<div class="flex gap-1">
								<CarouselGenericAction
									variant="dark"
									@click="scrollSlides(currentCarouselItemIndex - 1)"
								>
									<IconMaterialSymbolsLightChevronLeft
										width="28px"
										height="28px"
									/>
								</CarouselGenericAction>
								<CarouselGenericAction variant="dark">
									<IconMaterialSymbolsLightChevronRight
										width="28px"
										height="28px"
										@click="scrollSlides(currentCarouselItemIndex + 1)"
									/>
								</CarouselGenericAction>
							</div>
						</div>
					</div>
				</Carousel>
			</template>
		</Container>
	</section>
</template>
